import type { Section, Location, SectionFragment } from '@/generated/graphql';
import technicalStopoverResolver from '@/components/FlightSummary/TechnicalStopover/resolver';
import type { TechnicalStopExtended } from '@/components/FlightSummary/TechnicalStopover/resolver';
import type {
  LocationTyped,
  LocationType,
  LocationExtended,
  TripExtension,
  Trip,
  CompletedSection,
} from './types';

const extendLocation = (
  location: LocationTyped,
  terminal: Section['departureTerminal']
): LocationExtended => ({
  ...location,
  terminal,
  id: location.id + (terminal ? '_' + terminal : ''),
});

const addLocationType = (
  location: Location,
  type: LocationType
): LocationTyped => ({
  ...location,
  type,
});

const createTrip = (
  section: SectionFragment,
  {
    departure,
    destination,
    departureDate,
    arrivalDate,
    id,
    duration,
  }: TripExtension
): Trip => ({
  ...section,
  __typename: 'Trip',
  id,
  departure,
  destination,
  departureDate,
  arrivalDate,
  duration,
});

export default function sectionResolver(
  section: SectionFragment,
  isLastInSegment?: boolean,
  isFirstSegment?: boolean
): CompletedSection {
  const technicalStops: Array<TechnicalStopExtended> = [];
  const trips: Array<Trip> = [];
  const departure = extendLocation(
    addLocationType(
      section.departure,
      isFirstSegment ? 'absolute' : 'connection'
    ),
    section.departureTerminal
  );
  let lastDepartureLocation: LocationTyped = departure;
  const departureLocation = departure;
  let tripDepartureDate = section.departureDate;
  section.technicalStops.forEach((technicalStop, index) => {
    trips.push(
      createTrip(section, {
        id: `${section.id}_${index}`,
        departure: departureLocation,
        departureDate: tripDepartureDate,
        destination: addLocationType(technicalStop.location, 'connection'),
        arrivalDate: technicalStop.arrivalDate,
        duration: section.duration,
      })
    );
    technicalStops.push(technicalStopoverResolver(technicalStop));
    lastDepartureLocation = addLocationType(
      technicalStop.location,
      'connection'
    );
    tripDepartureDate = technicalStop.departureDate;
  });

  trips.push(
    createTrip(section, {
      id: `${section.id}_${technicalStops.length}`,
      departure: lastDepartureLocation,
      destination: extendLocation(
        addLocationType(
          section.destination,
          isLastInSegment ? 'absolute' : 'connection'
        ),
        section.arrivalTerminal
      ),
      departureDate: tripDepartureDate,
      arrivalDate: section.arrivalDate,
      duration: section.duration,
    })
  );

  return {
    ...section,
    trips,
    technicalStops,
  };
}
